/**
 * Top-of-page banner shown whenever the active session is an
 * impersonated one (a superuser is "logged in as" another user).
 *
 * Pinned across the top of the viewport, above the sidebar and the top
 * bar. It must be `fixed`, not a normal child: SidebarProvider's wrapper
 * is `flex` (a row), so an in-flow banner became a full-height column down
 * the left of the page, painting amber over the whole sidebar.
 *
 * Its height is published as `--impersonation-height`, which the topbar and
 * sidebar offset themselves by and which `--topbar-height` folds in, so
 * every full-height pane shifts down without knowing this banner exists.
 *
 * Persists between page navigations because impersonation state lives
 * in localStorage (`impersonation_meta`). Clicking "Return" restores
 * the original token + user and hard-reloads to flush every cached
 * query. We bounce the page rather than trying to surgically swap
 * react-query keys — the cleanest reset for an identity change.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { UserCog, X } from "lucide-react";
import { impersonationStore, ImpersonationMeta } from "@/services/platformAdminApi";

export const ImpersonationBanner = () => {
  const [meta, setMeta] = useState<ImpersonationMeta | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMeta(impersonationStore.meta());
    // Multiple tabs could enter/leave impersonation simultaneously —
    // listen on `storage` so other tabs' state changes propagate
    // without a refresh.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "impersonation_meta" || e.key === "auth_token_original") {
        setMeta(impersonationStore.meta());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Publish the height rather than hard-coding one: the message wraps to a
  // second line on narrow viewports, and a stale offset would tuck the
  // topbar underneath it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.setProperty("--impersonation-height", "0px");
    if (!meta) {
      clear();
      return;
    }
    const measure = () =>
      root.style.setProperty("--impersonation-height", `${ref.current?.offsetHeight ?? 0}px`);
    measure();
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      clear();
    };
  }, [meta]);

  if (!meta) return null;

  const onReturn = () => {
    impersonationStore.stop();
    window.location.assign("/admin/platform");
  };

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-50 bg-warning text-warning-foreground shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <UserCog className="w-4 h-4 shrink-0" />
          <span className="truncate">
            You're acting as <strong>{meta.acting_username}</strong> · Any changes are made under this account.
          </span>
        </div>
        <button
          onClick={onReturn}
          className="flex items-center gap-1 px-3 py-1 rounded-md bg-warning-foreground/90 text-warning hover:bg-warning-foreground transition-colors font-medium"
        >
          <X className="w-3.5 h-3.5" /> Return to your account
        </button>
      </div>
    </div>
  );
};
